import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { OverviewSandbox, SearchService } from '@mediahaven/shared';
import {
    ApiFragment,
    ApiQuery,
    CustomErrorType,
    DisplayMode,
    DottedKey,
    PaginatedResults,
    ProfileDataService,
    QueryPart,
    QueryPartStrategy,
    QueryStateHandler,
    RecordRestrictions,
    Restriction,
    SearchState,
    TernaryLogic
} from '@stylehaven/components';

@Injectable()
export abstract class RecordsOverviewSandbox extends OverviewSandbox {
    public useBatch$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // Injectors
    protected readonly searchService: SearchService = inject(SearchService);
    protected readonly queryStateHandler: QueryStateHandler = inject(QueryStateHandler);
    protected readonly profileService: ProfileDataService = inject(ProfileDataService);

    //#region Search
    public abstract getRestrictions(...args: any): Observable<Restriction>;

    protected getGeolocationRestriction(): Observable<Restriction> {
        return this.getUrlQueryState().pipe(
            map(state => state.view === DisplayMode.MAP),
            distinctUntilChanged(),
            map(isMap => RecordRestrictions.geolocationRestriction(isMap ? TernaryLogic.true : TernaryLogic.both))
        );
    }

    public getRecords(searchState: SearchState, fieldSelect: DottedKey[]): Observable<PaginatedResults<ApiFragment>> {
        const pagination = {
            page: searchState.page,
            resultsPerPage: searchState.pageSize
        };
        const sortInfo = searchState.sortInfo;

        return this.getRestrictions().pipe(
            switchMap(restrictions => {
                return this.searchService.getRecords(
                    pagination,
                    sortInfo,
                    new ApiQuery(searchState.structuredQuery, restrictions, fieldSelect)
                );
            })
        );
    }

    public getAllRecordsWithId(fragmentIds: string[]): Observable<ApiFragment[]> {
        if (fragmentIds.length === 0) {
            return of([]);
        }

        const restriction$: Observable<Restriction> = this.getRestrictions();

        const queryParts: QueryPart[] = [
            new QueryPart({
                field: 'FragmentId',
                strategy: QueryPartStrategy.MatchAtLeastOne,
                values: fragmentIds
            })
        ];

        const query$: Observable<ApiQuery> = restriction$.pipe(
            map(restriction => new ApiQuery(queryParts, restriction))
        );

        return query$.pipe(
            switchMap(query => this.searchService.getAllRecords(query)),
            catchError(this.handleRestrictionError)
        );
    }

    protected handleRestrictionError(error: Error): Observable<any[]> {
        console.error(error);

        // Catch ZoneRestriction error and just return an empty array of results
        if (error.name === CustomErrorType.ZoneRestrictions) {
            return of([]);
        }
        throw error;
    }

    //#endregion
}
